import { useState } from 'react'
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ApartmentOutlined,
  ClusterOutlined,
  DeleteOutlined,
  KeyOutlined,
  LockOutlined,
  LogoutOutlined,
  PlusOutlined,
  SafetyOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime } from '@/shared/lib/format'
import { roleName } from '@/shared/lib/roleName'
import { EmptyState } from '@/shared/ui/EmptyState'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { PageHeader } from '@/shared/ui/PageHeader'
import { AssignRoleModal } from '@/features/identity/AssignRoleModal'
import { PartyNodeModal } from '@/features/identity/PartyNodeModal'
import { UserNetworkModal } from '@/features/network/UserNetworkModal'
import { PasswordModal } from '@/features/identity/PasswordModal'
import { UserFormModal } from '@/features/identity/UserFormModal'
import { UserPermissionsPanel } from '@/features/identity/UserPermissionsPanel'
import { useOrgUnits } from '@/features/identity/useOrgUnits'
import {
  fetchAllRoles,
  fetchPartyNodeTarget,
  fetchPermissionCatalog,
  fetchUser,
  fetchUserPage,
  isPartner,
  logoutAllDevices,
  removeUserFromTenant,
  resetUserTotp,
  revokeRole,
  setMembershipState,
  setUserState,
  type PanelUser,
  type RoleAssignment,
} from '@/features/identity/identityApi'

const STATE_COLOR: Record<PanelUser['state'], string> = {
  ACTIVE: 'green',
  BLOCKED: 'red',
}

/**
 * "Doirasi bor, bog'lanish yo'q" — hech qanday xato bermaydigan
 * nosozlikning yagona ko'rinadigan izi.
 *
 * ⚠️ 2026-08-22 dan boshlab uni SHU SAHIFADA tuzatish mumkin:
 * «Tuzilmaga bog'lash» amali qaytarildi (egasi qarori — har hisob
 * tuzilmaning bir birligiga biriktiriladi). Ogohlantirish esa qoladi,
 * chunki holatning o'zi jim: odam panelga kiradi va bo'sh ekran
 * ko'radi.
 *
 * · SHERIK biriktirilmagan bo'lsa hech kimning abonentini ko'rmaydi va
 *   kabinet BO'SH ochiladi.
 *
 * · XODIMDA `OWN` doirali rol qolgan bo'lsa — bu ADR-029 dan keyingi
 *   ANOMALIYA: xodim rollarida `OWN` ishlatilmaydi. Bunday holatda ikki
 *   yo'l bor va ikkalasi ham shu sahifada: birlikka biriktirish yoki
 *   rolni to'g'ri doira bilan qaytadan berish.
 */
function needsPartyNode(user: PanelUser): boolean {
  if (user.partyNodeId !== null) return false
  return isPartner(user) || user.roles.some((role) => role.scopeType === 'OWN')
}

/**
 * XODIMLAR, ularning rollari va huquqlari.
 *
 * <h2>⚠️ SHERIK KO'RINADI, LEKIN BU YERDAN SOZLANMAYDI</h2>
 * Egasi qarori (2026-08-19): sherik agent yaratish oqimida qo'shiladi,
 * bu yerda esa faqat ROL tanlanadi. Sahifa bitta savolga javob beradi:
 * <b>bu xodim nima qila oladi</b>.
 *
 * Sherik qatorlarini BUTUNLAY yashirish RAD ETILDI va sabab aniq:
 * sherik hisobi baribir `identity.app_user` da yashaydi, ya'ni uni
 * BLOKLASH, PAROLINI TIKLASH, TOTP'ini tozalash va sessiyalarini uzish
 * kerak bo'ladi — bu amallarning boshqa uyi YO'Q (Agentlar sahifasi
 * tugunni boshqaradi, hisobni emas). Yashirilgan sherik "hisob yo'q"
 * degan yolg'on taassurot berardi va operator uni faqat bazadan
 * topardi.
 *
 * Shuning uchun sherik qatorlari ro'yxatda TURADI va «Sherik» yorlig'i
 * bilan ajratiladi; tanlangan sherikda esa HISOB amallari qoladi. Turi
 * va kassa bayrog'i bu yerda ko'rsatilmaydi — ular Agentlar sahifasida,
 * va kartochka o'sha yerga yuboradi.
 *
 * <h2>⚠️ TUZILMAGA BOG'LASH — SHU YERDA (2026-08-22)</h2>
 * Egasi qarori: har hisob — xodim, agent, reseller — tashkiliy
 * tuzilmaning bir BIRLIGIGA biriktiriladi va bu yagona joyda
 * boshqariladi. Birlikning O'ZI esa faqat «Tuzilma» menyusida
 * yaratiladi: bu ekran tanlaydi, yaratmaydi.
 *
 * <h2>⚠️ «FAQAT XODIMLAR» BELGILAGICHI OLIB TASHLANDI (2026-08-20)</h2>
 * U mijozda filtrlardi va yonida sheriklar SONINI ko'rsatardi — ikkalasi
 * ham TO'LIQ ro'yxat kelishiga tayanardi. `GET /api/users` endi
 * sahifalangan va serverda `kind` bo'yicha filtr YO'Q: belgilagich faqat
 * joriy sahifani toraytirar, son esa o'sha sahifadagi sheriklarni
 * sanardi. Ikkalasi ham ishonarli ko'rinadigan yolg'on bo'lardi.
 *
 * <h2>⚠️ SAHIFALASH, QIDIRUV VA SARALASH — SERVERDA</h2>
 * `UserResource#list` endi `page`/`size`/`search`/`sortField`/`sortOrder`
 * ni qabul qiladi va `{rows,total,page,size}` qaytaradi. Sukut tartib
 * ham o'zgardi: ilgari `username` (alifbo), endi `CREATED_AT DESC` —
 * oxirgi ochilgan hisob tepada.
 *
 * <h2>Bu sahifa boshqalardan farq qiladi</h2>
 * Bu yerda HUQUQ beriladi va olinadi, ya'ni xato qimmatga tushadi. Shuning
 * uchun uchta qoida qat'iy:
 *
 *   1. <b>Parol hech qachon ko'rsatilmaydi.</b> Server uni qaytarmaydi ham
 *      (`PasswordRequest` — faqat yozish, CLAUDE.md §7). Sahifada faqat
 *      "o'zgartirish" bor.
 *   2. <b>Boshqa odamning kirishini buzadigan amallar tasdiqlash oynasidan
 *      o'tadi</b> — TOTP reset va "barcha qurilmalardan chiqarish". Ular
 *      qaytarib bo'lmaydigan yoki og'riqli: birinchisidan keyin odam QR'ni
 *      qaytadan skanerlashi kerak.
 *   3. <b>O'zini qulflab qo'yish TO'SILGAN.</b> Foydalanuvchi o'z hisobini
 *      bloklay olmaydi va o'z rolini tortib ololmaydi. Sabab: oxirgi admin
 *      shu yo'l bilan butun o'rnatmadan chiqib ketishi mumkin va uni
 *      panel orqali TIKLAB BO'LMAYDI — faqat bazaga qo'lda kirish qoladi.
 *      Backendda bunday to'siq YO'Q (`UserServiceImpl.setState` faqat
 *      a'zolikni tekshiradi), shuning uchun bu yerdagisi haqiqiy himoya
 *      — odatdagi "qulaylik uchun yashirish" emas.
 *
 * <h2>A'zolik faqat JORIY tashkilot uchun</h2>
 * Odam bir nechta tashkilotda ishlashi mumkin (`identity.user_tenant`),
 * lekin `GET /api/users` faqat JORIY tenant a'zolarini beradi — bu RLS
 * chegarasi, qulaylik emas (`UserServiceImpl.listUsers` javadoc'i).
 * Boshqa tashkilotdagi a'zoliklarini ko'rsatadigan endpoint YO'Q, shuning
 * uchun panel ularni TAXMIN QILMAYDI va farqni aniq yozadi:
 *   · HISOB holati (`state`) — barcha tashkilotlarga ta'sir qiladi
 *   · A'ZOLIK holati (`membershipState`) — faqat shu tashkilotga
 */

/** Uzun «rol · doira · filial yo'li» qo'shni ustun ustiga chiqmasin */
const ASSIGNMENT_TAG_STYLE = { whiteSpace: 'normal', marginInlineEnd: 0 } as const
export function UsersPage() {
  const { t } = useTranslation()
  const { user: actor } = useAuth()
  const { can } = usePermissions()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()

  /**
   * Ro'yxatda BOSILGAN qator — o'ng tomondagi kartochkaning boshlang'ich
   * ko'rinishi.
   *
   * ⚠️ Faqat `id` SAQLASH endi YETMAYDI: ro'yxat sahifalangan va qator
   * boshqa sahifada qolishi mumkin. Shuning uchun bu yerda butun qator
   * turadi, kartochka esa uni serverdan yangilab oladi (`detail`).
   */
  const [picked, setPicked] = useState<PanelUser | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [roleOpen, setRoleOpen] = useState(false)
  const [partyNodeOpen, setPartyNodeOpen] = useState(false)
  const [networkOpen, setNetworkOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ⚠️ Bu QULAYLIK, xavfsizlik EMAS — backend har so'rovda qayta tekshiradi.
  // Yagona maqsad: 403 bilan tugaydigan tugma ko'rsatmaslik.
  const canReadUsers = can('user.read')
  const canReadRoles = can('role.read')
  const canUpdate = can('user.update')
  /**
   * ⚠️ `user.role.assign`, `user.update` EMAS — serverdagi bilan bir xil
   * (`UserResource#setPartyNode`): bog'lanish odamning KIRISHIGA emas,
   * NIMA KO'RISHIGA tegadi.
   */
  const canBind = can('user.role.assign')

  /*
    Tuzilma birliklari — bog'lanishni NOM bilan ko'rsatish uchun.
    Kesh Tuzilma sahifasiniki bilan bitta, ya'ni qo'shimcha so'rov
    faqat birinchi ochilishda ketadi.
  */
  const orgUnits = useOrgUnits(canReadUsers)

  /*
    ⚠️ ROLLAR KATALOGI TO'LIQ olinadi va bu ataylab: u TANLAGICH
    («rol berish» oynasi), jadval emas. Bir sahifa ko'rsatish operatorga
    mavjud rolni "yo'q" deb ko'rsatardi.
  */
  const roles = useQuery({
    queryKey: queryKeys.identity.roles,
    queryFn: fetchAllRoles,
    enabled: canReadRoles,
    staleTime: 5 * 60_000,
  })

  const assignmentRoleName = (assignment: RoleAssignment): string => {
    const role = (roles.data ?? []).find((r) => r.id === assignment.roleId)
    return role ? roleName(role, t) : t(`roles.systemName.${assignment.roleCode}`, { defaultValue: assignment.roleCode })
  }

  const assignmentScope = (assignment: RoleAssignment): string => {
    const where = assignment.scopeType === 'SUBTREE' ? orgUnits.pathOf(assignment.scopeId) : null
    return [t(`identity.scope.${assignment.scopeType}`), where].filter(Boolean).join(' · ')
  }

  const assignmentLabel = (assignment: RoleAssignment): string =>
    `${assignmentRoleName(assignment)} · ${assignmentScope(assignment)}`

  const catalog = useQuery({
    queryKey: queryKeys.identity.permissionCatalog,
    queryFn: fetchPermissionCatalog,
    enabled: canReadRoles,
    staleTime: Number.POSITIVE_INFINITY,
  })

  /*
    ⚠️ KARTOCHKA SERVERDAN YANGILANADI, bosilgan qatordan EMAS.

    Ro'yxat sahifalangan: mutatsiyadan keyin qator boshqa sahifaga
    o'tishi yoki filtrdan chiqib ketishi mumkin va o'shanda kartochka
    ESKIRGAN nusxani ko'rsatardi — bloklangan hisob "faol" bo'lib
    turardi. So'rov yiqilsa (404, huquq) bosilgan qator zaxira bo'lib
    qoladi: kartochka bo'sh qolgandan ko'ra eski, lekin ko'rinadigan
    ma'lumot afzal.
  */
  const detail = useQuery({
    queryKey: queryKeys.identity.user(picked?.id ?? ''),
    queryFn: () => fetchUser(picked?.id ?? ''),
    enabled: picked !== null,
    retry: false,
  })

  const selected = detail.data ?? picked
  const isSelf = selected !== null && actor !== null && selected.id === actor.id
  /**
   * ⚠️ Sherikka rol berib bo'lmaydi — server 409 bilan rad etadi
   * (ADR-029: uning huquqlari kodda, `identity.user_role` da emas).
   * Bu yerdagisi QULAYLIK: 409 bilan tugaydigan tugma ko'rsatmaslik.
   */
  const selectedIsPartner = selected !== null && isPartner(selected)

  /**
   * Bog'langan agentning NOMI — `UserView` faqat `partyNodeId` beradi.
   * UUID operatorga hech narsa aytmaydi va tanlov ham nom bo'yicha
   * bo'ladi, ya'ni ikkalasi bir tilda gapirishi kerak.
   */
  const partyNode = useQuery({
    queryKey: queryKeys.agents.detail(selected?.partyNodeId ?? ''),
    queryFn: () => fetchPartyNodeTarget(selected?.partyNodeId ?? ''),
    enabled: selected?.partyNodeId != null,
    staleTime: 5 * 60_000,
  })

  /**
   * Ro'yxat ham, ochiq kartochka ham yangilanadi.
   *
   * ⚠️ IKKALASI HAM KERAK: ular endi ALOHIDA so'rov (`identity.users`
   * prefiksi jadvalniki, `identity.user(id)` kartochkaniki). Faqat
   * birinchisini bekor qilish kartochkani eskirgan holda qoldirardi.
   */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.identity.users })
    if (picked !== null) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.identity.user(picked.id) })
    }
  }

  const stateMutation = useMutation({
    mutationFn: (input: { id: string; state: PanelUser['state'] }) =>
      setUserState(input.id, input.state),
    onSuccess: (_data, input) => {
      refresh()
      void message.success(
        input.state === 'BLOCKED' ? t('identity.actions.blockDone') : t('identity.actions.unblockDone'),
      )
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const membershipMutation = useMutation({
    mutationFn: (input: { id: string; state: PanelUser['membershipState'] }) =>
      setMembershipState(input.id, input.state),
    onSuccess: (_data, input) => {
      refresh()
      void message.success(
        input.state === 'BLOCKED'
          ? t('identity.actions.membershipBlockDone')
          : t('identity.actions.membershipUnblockDone'),
      )
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => removeUserFromTenant(id),
    onSuccess: () => {
      refresh()
      // Tanlangan kartochkani yopamiz — odam endi ro'yxatda yo'q va
      // ochiq qolgan kartochka "bor" degan yolg'on taassurot berardi.
      setPicked(null)
      void message.success(t('identity.actions.removeDone'))
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const revoke = useMutation({
    mutationFn: (input: { id: string; assignment: RoleAssignment }) =>
      revokeRole(input.id, {
        roleId: input.assignment.roleId,
        scopeType: input.assignment.scopeType,
        scopeId: input.assignment.scopeId,
      }),
    onSuccess: () => {
      refresh()
      void message.success(t('identity.roles.revokeDone'))
    },
    onError: (err) => setError(translateError(err, t)),
  })

  /**
   * TOTP reset — qaytarib bo'lmaydigan amal: eski autentifikator yozuvi
   * ishlamay qoladi va odam keyingi kirishda QR'ni qaytadan skanerlaydi.
   * Shuning uchun TASDIQLASH majburiy.
   */
  const confirmTotpReset = (target: PanelUser) => {
    modal.confirm({
      title: t('identity.actions.totpResetTitle'),
      content: (
        <>
          <Typography.Paragraph>
            {t('identity.actions.totpResetConfirm', { username: target.username })}
          </Typography.Paragraph>
          {actor !== null && target.id === actor.id ? (
            <Alert type="warning" showIcon message={t('identity.actions.totpResetSelfWarning')} />
          ) : null}
        </>
      ),
      okButtonProps: { danger: true },
      okText: t('identity.actions.totpReset'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await resetUserTotp(target.id)
          void message.success(t('identity.actions.totpResetDone'))
        } catch (err) {
          setError(translateError(err, t))
          throw err
        }
      },
    })
  }

  /**
   * Barcha qurilmalardan chiqarish — hisobni YOPMAYDI, faqat joriy
   * sessiyalarni uzadi. Baribir tasdiqlanadi: odam ish o'rtasida
   * chiqib qoladi.
   */
  const confirmLogoutAll = (target: PanelUser) => {
    modal.confirm({
      title: t('identity.actions.logoutAllTitle'),
      content: (
        <>
          <Typography.Paragraph>
            {t('identity.actions.logoutAllConfirm', { username: target.username })}
          </Typography.Paragraph>
          {actor !== null && target.id === actor.id ? (
            <Alert type="warning" showIcon message={t('identity.actions.logoutAllSelfWarning')} />
          ) : null}
        </>
      ),
      okButtonProps: { danger: true },
      okText: t('identity.actions.logoutAll'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await logoutAllDevices(target.id)
          void message.success(t('identity.actions.logoutAllDone'))
        } catch (err) {
          setError(translateError(err, t))
          throw err
        }
      },
    })
  }

  const confirmStateChange = (target: PanelUser, next: PanelUser['state']) => {
    modal.confirm({
      title: next === 'BLOCKED' ? t('identity.actions.blockTitle') : t('identity.actions.unblockTitle'),
      content:
        next === 'BLOCKED'
          ? t('identity.actions.blockConfirm', { username: target.username })
          : t('identity.actions.unblockConfirm', { username: target.username }),
      okButtonProps: { danger: next === 'BLOCKED' },
      okText: next === 'BLOCKED' ? t('identity.actions.block') : t('identity.actions.unblock'),
      cancelText: t('common.cancel'),
      onOk: () => stateMutation.mutateAsync({ id: target.id, state: next }),
    })
  }

  const confirmMembershipChange = (target: PanelUser, next: PanelUser['membershipState']) => {
    modal.confirm({
      title:
        next === 'BLOCKED'
          ? t('identity.actions.membershipBlockTitle')
          : t('identity.actions.membershipUnblockTitle'),
      content:
        next === 'BLOCKED'
          ? t('identity.actions.membershipBlockConfirm', { username: target.username })
          : t('identity.actions.membershipUnblockConfirm', { username: target.username }),
      okButtonProps: { danger: next === 'BLOCKED' },
      okText:
        next === 'BLOCKED'
          ? t('identity.actions.membershipBlock')
          : t('identity.actions.membershipUnblock'),
      cancelText: t('common.cancel'),
      onOk: () => membershipMutation.mutateAsync({ id: target.id, state: next }),
    })
  }

  /**
   * ⚠️ "Ishonchingiz komilmi?" YETARLI EMAS — oyna aynan NIMA
   * YO'QOLISHINI va NIMA QOLISHINI aytadi.
   *
   * Sabab: bu amal a'zolik bloklashdan farqli o'laroq QAYTARIB
   * BO'LMAYDI — rol grantlari (kim, qachon, qaysi doirada bergan)
   * yo'qoladi va qayta tiklash "yangidan berish" bo'ladi. Foydalanuvchi
   * buni bosishdan OLDIN bilishi kerak, keyin emas.
   *
   * Ikkinchi qator ham xuddi shunchalik muhim: hisob va tarix
   * SAQLANADI. Usiz operator "odamni butunlay o'chirdim" deb o'ylardi va
   * bu yolg'on bo'lardi.
   */
  const confirmRemove = (target: PanelUser) => {
    modal.confirm({
      title: t('identity.actions.removeTitle', { username: target.username }),
      width: 520,
      content: (
        <Space direction="vertical" size={8} style={{ marginTop: 8 }}>
          <Alert
            type="warning"
            showIcon
            message={t('identity.actions.removeLosesTitle')}
            description={t('identity.actions.removeLoses', {
              roles: target.roles.length,
            })}
          />
          <Alert
            type="info"
            showIcon
            message={t('identity.actions.removeKeepsTitle')}
            description={t('identity.actions.removeKeeps')}
          />
          <Typography.Text type="secondary">
            {t('identity.actions.removeAlternative')}
          </Typography.Text>
        </Space>
      ),
      okButtonProps: { danger: true },
      okText: t('identity.actions.remove'),
      cancelText: t('common.cancel'),
      onOk: () => removeMutation.mutateAsync(target.id),
    })
  }

  const confirmRevoke = (target: PanelUser, assignment: RoleAssignment) => {
    modal.confirm({
      title: t('identity.roles.revokeTitle'),
      content: t('identity.roles.revokeConfirm', {
        role: assignmentRoleName(assignment),
        username: target.username,
      }),
      okButtonProps: { danger: true },
      okText: t('identity.roles.revoke'),
      cancelText: t('common.cancel'),
      onOk: () => revoke.mutateAsync({ id: target.id, assignment }),
    })
  }

  const columns: ColumnsType<PanelUser> = [
    {
      key: 'username',
      title: t('identity.columns.username'),
      sorter: true,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <Typography.Text strong>{row.username}</Typography.Text>
            {actor !== null && row.id === actor.id ? (
              <Tag color="blue">{t('identity.self.badge')}</Tag>
            ) : null}
          </Space>
          {row.displayName !== row.username ? (
            <Typography.Text type="secondary">{row.displayName}</Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'roles',
      title: t('identity.columns.roles'),
      render: (_, row) =>
        /* ⚠️ SHERIK — alohida shox. Uning TURI va kassa bayrog'i bu
           yerda KO'RSATILMAYDI: ular Agentlar sahifasida boshqariladi
           va bu ustunda ikkinchi haqiqat manbai bo'lardi. Qoladigan
           narsa — «bu qator xodim emas» degan bitta belgi va haqiqiy
           ANOMALIYALAR. «Rolsiz» belgisi esa sherikda YOLG'ON bo'lardi:
           unda rol bo'lmasligi TO'G'RI holat. */
        isPartner(row) ? (
          <Space size={[4, 4]} wrap>
            <Tooltip title={t('identity.partner.listHint')}>
              <Tag color="purple">{t('identity.userKind.PARTNER')}</Tag>
            </Tooltip>
            {/* ⚠️ Sherikda rol BO'LMASLIGI kerak (migratsiya ularni
                o'chirgan, server yangisini rad etadi). Qolgani —
                anomaliya: uning huquqlariga ta'sir qilmaydi, lekin
                keyingi deployning o'zini tekshirish bo'limini
                yiqitadi. Shuning uchun u YASHIRILMAYDI. */}
            {row.roles.map((assignment) => (
              <Tag
                key={`${assignment.roleId}-${assignment.scopeType}-${assignment.scopeId ?? ''}`}
                color="red"
                style={ASSIGNMENT_TAG_STYLE}
              >
                {assignmentLabel(assignment)}
              </Tag>
            ))}
            {needsPartyNode(row) ? (
              <Tooltip title={t('identity.partyNode.partnerMissingHint')}>
                <Tag color="orange">{t('identity.partyNode.missing')}</Tag>
              </Tooltip>
            ) : null}
          </Space>
        ) : row.roles.length === 0 ? (
          // "Rolsiz" — bo'sh katak emas, ANIQ holat: odam kira oladi, lekin
          // hech narsa ko'rmaydi. Buni ko'rsatmaslik "sozlash unutilgan"
          // hisoblarni ko'rinmas qilardi.
          <Tag color="orange">{t('identity.roles.none')}</Tag>
        ) : (
          <Space size={[4, 4]} wrap>
            {row.roles.map((assignment) => (
              <Tag
                key={`${assignment.roleId}-${assignment.scopeType}-${assignment.scopeId ?? ''}`}
                style={ASSIGNMENT_TAG_STYLE}
              >
                {assignmentLabel(assignment)}
              </Tag>
            ))}
            {/* ⚠️ ENG JIM NUQSON: `OWN` doiradagi rol berilgan, lekin
                agentga bog'lanmagan odam panelga KIRADI va hech narsa
                ko'rmaydi — na shajarasini, na komissiyasini. Hech qayerda
                xato bo'lmaydi, shuning uchun belgi ro'yxatda turadi. */}
            {needsPartyNode(row) ? (
              <Tooltip title={t('identity.partyNode.missingHint')}>
                <Tag color="orange">{t('identity.partyNode.missing')}</Tag>
              </Tooltip>
            ) : null}
          </Space>
        ),
    },
    {
      key: 'state',
      title: t('identity.columns.state'),
      width: 120,
      sorter: true,
      render: (_, row) => (
        <Tooltip title={t(`identity.stateHint.${row.state}`)}>
          <Tag color={STATE_COLOR[row.state]}>{t(`identity.state.${row.state}`)}</Tag>
        </Tooltip>
      ),
    },
    {
      key: 'membership',
      title: t('identity.columns.membership'),
      width: 140,
      render: (_, row) => (
        <Tooltip title={t(`identity.membershipHint.${row.membershipState}`)}>
          <Tag color={row.membershipState === 'ACTIVE' ? 'green' : 'gold'}>
            {t(`identity.membership.${row.membershipState}`)}
          </Tag>
        </Tooltip>
      ),
    },
    {
      key: 'lastLogin',
      title: t('identity.columns.lastLogin'),
      width: 180,
      sorter: true,
      render: (_, row) =>
        row.lastLoginAt === null ? (
          <Typography.Text type="secondary">{t('identity.detail.neverLoggedIn')}</Typography.Text>
        ) : (
          formatDateTime(row.lastLoginAt)
        ),
    },
    {
      /*
        HISOB amallari — qatorning O'ZIDA.

        ⚠️ BU YERDA YANGI AMAL YO'Q va yangi huquq ham yo'q. Menyu
        o'ng tomondagi kartochkadagi AYNAN o'sha `confirm*`
        funksiyalarini chaqiradi — ya'ni tasdiqlash oynalari, matnlari
        va serverga ketadigan so'rov bir xil. Ikkinchi nusxa yozilsa
        ular jimgina ajralib ketardi: kartochkada tasdiq bor,
        menyuda yo'q.

        ⚠️ UCHTA HIMOYA SAQLANDI (sinf javadoc'i):
          1. O'ZINI bloklash / a'zolikdan chiqarish — `disabled`, va
             sababi yorliqdagi tooltipda. Yashirish "nega yo'q" degan
             savol qoldirardi.
          2. SHERIKKA rol berish — band UMUMAN chizilmaydi (ADR-029:
             sherik huquqlari KODDA, server 409 qaytaradi).
          3. `user.delete` — alohida huquq, `user.update` EMAS.

        ⚠️ Parol o'zgartirish oynasi TANLANGAN qatorga bog'langan
        (`PasswordModal user={selected}`), shuning uchun band avval
        qatorni tanlaydi. Aks holda menyu boshqa odamning parolini
        ochardi — bu sahifada eng qimmat xato bo'lardi.
      */
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => {
        const rowIsSelf = actor !== null && row.id === actor.id
        const selfHint = (label: string) =>
          rowIsSelf ? (
            <Tooltip title={t('identity.self.cannotBlock')} placement="left">
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          )
        return (
          <RowActions
            items={[
              {
                key: 'password',
                label: t('identity.password.button'),
                icon: <KeyOutlined />,
                permission: 'user.update',
                onClick: () => {
                  setPicked(row)
                  setPasswordOpen(true)
                },
              },
              {
                key: 'assignRole',
                label: t('identity.roles.assign'),
                permission: 'user.role.assign',
                // Sherikda rol tushunchasi YO'Q — band ko'rinmaydi.
                // Rollar katalogisiz oyna bo'sh ochilardi.
                hidden: isPartner(row) || !canReadRoles,
                onClick: () => {
                  setPicked(row)
                  setRoleOpen(true)
                },
              },
              {
                /*
                  ⚠️ O'ZINI bog'lash serverda 403 `PRIVILEGE_ESCALATION`
                  bilan to'siladi (Staging'da o'lchandi). Band
                  YASHIRILMAYDI, o'chiriladi — yashirilgani "nega yo'q"
                  degan javobsiz savol qoldirardi.
                */
                key: 'partyNode',
                label: rowIsSelf ? (
                  <Tooltip title={t('identity.partyNode.selfBlocked')} placement="left">
                    <span>{t('identity.partyNode.bind')}</span>
                  </Tooltip>
                ) : (
                  t('identity.partyNode.bind')
                ),
                icon: <ApartmentOutlined />,
                disabled: rowIsSelf,
                permission: 'user.role.assign',
                onClick: () => {
                  // ⚠️ Oyna TANLANGAN qatorga bog'langan, shuning uchun
                  // qator avval tanlanadi — aks holda oyna boshqa
                  // odamning bog'lanishini o'zgartirardi.
                  setPicked(row)
                  setPartyNodeOpen(true)
                },
              },
              {
                key: 'network',
                label: t('network.userAssignment.action'),
                icon: <ClusterOutlined />,
                permission: 'network.visp.manage',
                hidden: isPartner(row),
                onClick: () => {
                  setPicked(row)
                  setNetworkOpen(true)
                },
              },
              {
                key: 'state',
                label: selfHint(
                  row.state === 'ACTIVE'
                    ? t('identity.actions.block')
                    : t('identity.actions.unblock'),
                ),
                icon: row.state === 'ACTIVE' ? <LockOutlined /> : <UnlockOutlined />,
                danger: row.state === 'ACTIVE',
                disabled: rowIsSelf,
                permission: 'user.update',
                divider: true,
                onClick: () =>
                  confirmStateChange(row, row.state === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE'),
              },
              {
                key: 'membership',
                label: selfHint(
                  row.membershipState === 'ACTIVE'
                    ? t('identity.actions.membershipBlock')
                    : t('identity.actions.membershipUnblock'),
                ),
                disabled: rowIsSelf,
                permission: 'user.update',
                onClick: () =>
                  confirmMembershipChange(
                    row,
                    row.membershipState === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE',
                  ),
              },
              {
                key: 'totpReset',
                label: t('identity.actions.totpReset'),
                icon: <SafetyOutlined />,
                permission: 'user.update',
                onClick: () => confirmTotpReset(row),
              },
              {
                key: 'logoutAll',
                label: t('identity.actions.logoutAll'),
                icon: <LogoutOutlined />,
                permission: 'user.update',
                onClick: () => confirmLogoutAll(row),
              },
              {
                key: 'remove',
                label: rowIsSelf ? (
                  <Tooltip title={t('identity.self.cannotRemove')} placement="left">
                    <span>{t('identity.actions.remove')}</span>
                  </Tooltip>
                ) : (
                  t('identity.actions.remove')
                ),
                icon: <DeleteOutlined />,
                danger: true,
                divider: true,
                disabled: rowIsSelf,
                permission: 'user.delete',
                onClick: () => confirmRemove(row),
              },
            ]}
          />
        )
      },
    },
  ]

  if (!canReadUsers) {
    return (
      <>
        <PageHeader title={t('nav.users')} description={t('identity.intro')} />
        <Card>
          <EmptyState
            icon={<TeamOutlined />}
            title={t('identity.noUserRead.title')}
            description={t('identity.noUserRead.text')}
          />
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={t('nav.users')}
        description={t('identity.intro')}
        extra={
          <Can permission="user.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('identity.create.button')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {!canReadRoles ? (
        <Alert
          type="info"
          showIcon
          message={t('identity.permissions.needRoleRead')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          {/*
            ⚠️ QIDIRUV, SARALASH VA SAHIFALASH — SERVERDA (sinf javadoc'i).
            Ro'yxat xatosi, bo'sh holat va «filtrni tozalash» ham
            `DataTable` ning o'zida — ilgari bu yerda ularning ikkinchi
            nusxasi turardi.
          */}
          <DataTable<PanelUser>
            queryKey={queryKeys.identity.users}
            fetchPage={fetchUserPage}
            columns={columns}
            rowKey={(row) => row.id}
            tableId="identity-users"
            stickyFirstColumn
            searchable
            searchPlaceholder={t('identity.searchPlaceholder')}
            size="small"
            scrollX={956}
            emptyTitle={t('identity.emptyTitle')}
            emptyDescription={t('identity.emptyDescription')}
            {...(can('user.create')
              ? {
                  emptyAction: {
                    label: t('identity.create.button'),
                    onClick: () => setCreateOpen(true),
                  },
                }
              : {})}
            /*
              ⚠️ Tanlangan qator `className` orqali belgilanadi, `Table`
              ning `rowClassName` i orqali emas: `DataTable` uni tashqariga
              chiqarmaydi va uni shu bitta sahifa uchun qo'shish 20+
              jadvalga tegish degani bo'lardi.
            */
            onRow={(row) => ({
              style: { cursor: 'pointer' },
              className: row.id === picked?.id ? 'ant-table-row-selected' : undefined,
              onClick: () => setPicked(row),
            })}
          />
        </Col>

        <Col xs={24} xl={10}>
          {selected === null ? (
            <Card>
              <EmptyState
                icon={<UserOutlined />}
                title={t('identity.selectHintTitle')}
                description={t('identity.selectHint')}
              />
            </Card>
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card
                title={
                  <Space size={4}>
                    <span>{selected.username}</span>
                    {isSelf ? <Tag color="blue">{t('identity.self.badge')}</Tag> : null}
                    {selectedIsPartner ? (
                      <Tag color="purple">{t('identity.userKind.PARTNER')}</Tag>
                    ) : null}
                  </Space>
                }
              >
                {/* ⚠️ Sherik kartochkasi BIRINCHI navbatda «bu yerda emas»
                    deyishi kerak: aks holda operator turini yoki
                    bog'lanishini shu yerdan qidirib, topa olmay qolardi.
                    Havola FAQAT huquqi borga ko'rsatiladi — yo'qida u
                    "kirish yo'q" sahifasiga olib borardi. */}
                {selectedIsPartner ? (
                  <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 16 }}>
                    <Alert
                      type="info"
                      showIcon
                      message={t('identity.partner.managedTitle')}
                      description={
                        <Space direction="vertical" size={4}>
                          <span>{t('identity.partner.managedText')}</span>
                          {can('agent.read') ? (
                            <Link to="/agents">{t('identity.partner.openInAgents')}</Link>
                          ) : null}
                        </Space>
                      }
                    />
                    {/* Bog'lanmagan sherik kabinetni BO'SH ko'radi va bu
                        hech qayerda xato bermaydi — belgi shu yerda
                        turishining yagona sababi shu. */}
                    {needsPartyNode(selected) ? (
                      <Alert
                        type="warning"
                        showIcon
                        message={t('identity.partyNode.missing')}
                        description={t('identity.partyNode.partnerMissingHint')}
                      />
                    ) : null}
                  </Space>
                ) : null}

                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label={t('identity.columns.displayName')}>
                    {selected.displayName}
                  </Descriptions.Item>
                  {/* ⚠️ HAR hisobda ko'rsatiladi, sherikda ham xodimda ham
                      (egasi qarori, 2026-08-22). Ilgari u faqat sherikda
                      yoki qiymat bo'lganda chizilardi — ya'ni eng muhim
                      holat, «biriktirilmagan xodim», umuman KO'RINMASDI. */}
                  <Descriptions.Item label={t('identity.partyNode.label')}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      {selected.partyNodeId === null ? (
                        <Typography.Text type="secondary">
                          {t('identity.partyNode.none')}
                        </Typography.Text>
                      ) : (
                        /*
                          Nom uch manbadan izlanadi va oxirgisi — id.
                          Bo'sh qoldirish "bog'lanish yo'q" degan YOLG'ON
                          taassurot berardi.

                          ⚠️ Zaxira manba (`partyNode`) ATAYLAB saqlandi:
                          u `agent.read` bilan ishlaydi, tuzilma ro'yxati
                          esa `org.node.read` talab qiladi. Ikkinchisi
                          yo'q operator aks holda faqat UUID ko'rardi.
                        */
                        (orgUnits.pathOf(selected.partyNodeId) ??
                        partyNode.data?.label ??
                        selected.partyNodeId)
                      )}
                      {canBind ? (
                        <Tooltip title={isSelf ? t('identity.partyNode.selfBlocked') : undefined}>
                          <Button
                            size="small"
                            icon={<ApartmentOutlined />}
                            disabled={isSelf}
                            onClick={() => setPartyNodeOpen(true)}
                          >
                            {t('identity.partyNode.bind')}
                          </Button>
                        </Tooltip>
                      ) : null}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('identity.detail.accountState')}>
                    <Tooltip title={t(`identity.stateHint.${selected.state}`)}>
                      <Tag color={STATE_COLOR[selected.state]}>
                        {t(`identity.state.${selected.state}`)}
                      </Tag>
                    </Tooltip>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('identity.detail.membership')}>
                    <Tooltip title={t(`identity.membershipHint.${selected.membershipState}`)}>
                      <Tag color={selected.membershipState === 'ACTIVE' ? 'green' : 'gold'}>
                        {t(`identity.membership.${selected.membershipState}`)}
                      </Tag>
                    </Tooltip>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('identity.detail.createdAt')}>
                    {selected.createdAt === null ? t('common.dash') : formatDateTime(selected.createdAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('identity.detail.lastLogin')}>
                    {selected.lastLoginAt === null
                      ? t('identity.detail.neverLoggedIn')
                      : formatDateTime(selected.lastLoginAt)}
                  </Descriptions.Item>
                </Descriptions>

                {/* Ko'p tashkilotlilik: bu odam boshqa tashkilotlarda ham
                    ishlashi mumkin, lekin panel ularni KO'RA OLMAYDI (RLS).
                    Shuning uchun ikki holatning farqi shu yerda aytiladi —
                    aks holda operator "bloklash" tugmasi qo'shni tashkilotni
                    ham to'xtatib qo'yishini bilmasdi. */}
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                  message={t('identity.detail.multiTenantTitle')}
                  description={t('identity.detail.multiTenantText')}
                />

                {canUpdate ? (
                  <Space wrap style={{ marginTop: 16 }}>
                    <Button
                      size="small"
                      icon={<KeyOutlined />}
                      onClick={() => setPasswordOpen(true)}
                    >
                      {t('identity.password.button')}
                    </Button>

                    {/* ⚠️ O'ZINI BLOKLASH TO'SILGAN — sabab sinf javadoc'ida.
                        Tugma YASHIRILMAYDI, o'chiriladi va sababi tooltipda:
                        yashirilgan tugma "nega yo'q" degan savol qoldiradi. */}
                    <Tooltip title={isSelf ? t('identity.self.cannotBlock') : undefined}>
                      <Button
                        size="small"
                        danger={selected.state === 'ACTIVE'}
                        disabled={isSelf}
                        icon={selected.state === 'ACTIVE' ? <LockOutlined /> : <UnlockOutlined />}
                        onClick={() =>
                          confirmStateChange(
                            selected,
                            selected.state === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE',
                          )
                        }
                      >
                        {selected.state === 'ACTIVE'
                          ? t('identity.actions.block')
                          : t('identity.actions.unblock')}
                      </Button>
                    </Tooltip>

                    <Tooltip title={isSelf ? t('identity.self.cannotBlock') : undefined}>
                      <Button
                        size="small"
                        disabled={isSelf}
                        onClick={() =>
                          confirmMembershipChange(
                            selected,
                            selected.membershipState === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE',
                          )
                        }
                      >
                        {selected.membershipState === 'ACTIVE'
                          ? t('identity.actions.membershipBlock')
                          : t('identity.actions.membershipUnblock')}
                      </Button>
                    </Tooltip>

                    <Button
                      size="small"
                      icon={<SafetyOutlined />}
                      onClick={() => confirmTotpReset(selected)}
                    >
                      {t('identity.actions.totpReset')}
                    </Button>

                    <Button
                      size="small"
                      icon={<LogoutOutlined />}
                      onClick={() => confirmLogoutAll(selected)}
                    >
                      {t('identity.actions.logoutAll')}
                    </Button>

                    {/* ⚠️ Alohida huquq (`user.delete`), `user.update` EMAS —
                        bloklash qaytariladi, bu esa yo'q. Tugma `Can` ichida:
                        huquqi yo'q odamda umuman ko'rinmaydi. O'zini
                        chiqarish esa serverda ham to'silgan (400). */}
                    <Can permission="user.delete">
                      <Tooltip title={isSelf ? t('identity.self.cannotRemove') : undefined}>
                        <Button
                          size="small"
                          danger
                          disabled={isSelf}
                          icon={<DeleteOutlined />}
                          onClick={() => confirmRemove(selected)}
                        >
                          {t('identity.actions.remove')}
                        </Button>
                      </Tooltip>
                    </Can>
                  </Space>
                ) : (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                    message={t('identity.actions.needUserUpdate')}
                  />
                )}
              </Card>

              {/* ⚠️ Sherikda ROLLAR kartasi UMUMAN CHIZILMAYDI — unda rol
                  yo'q va berilmaydi ham. Yagona istisno: anomaliya, ya'ni
                  migratsiyadan qolib ketgan grant. U ko'rsatiladi va aynan
                  shu yerdan olib tashlanadi — boshqa yo'l yo'q. */}
              {selectedIsPartner && selected.roles.length === 0 ? null : (
                <Card
                  title={t('identity.roles.title')}
                  extra={
                    selectedIsPartner ? null : (
                      <Can permission="user.role.assign">
                        <Tooltip
                          title={canReadRoles ? undefined : t('identity.roles.needRoleRead')}
                        >
                          <Button
                            size="small"
                            disabled={!canReadRoles}
                            onClick={() => setRoleOpen(true)}
                          >
                            {t('identity.roles.assign')}
                          </Button>
                        </Tooltip>
                      </Can>
                    )
                  }
                >
                  {selectedIsPartner ? (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={t('identity.roles.partnerAnomalyTitle')}
                      description={t('identity.roles.partnerAnomalyText')}
                    />
                  ) : null}
  
                  {/* ⚠️ Xodimda `OWN` doirali rol — ADR-029 dan keyingi
                      ANOMALIYA. Ikki tuzatish yo'li ham shu sahifada:
                      rolni olib tashlash (shu karta) yoki tuzilmaga
                      biriktirish (yuqoridagi kartochka). */}
                  {!selectedIsPartner && needsPartyNode(selected) ? (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={t('identity.partyNode.missing')}
                      description={t('identity.partyNode.missingHint')}
                    />
                  ) : null}
  
                  {selected.roles.length === 0 ? (
                    <EmptyState
                      icon={<SafetyOutlined />}
                      title={t('identity.roles.emptyTitle')}
                      description={t('identity.roles.emptyDescription')}
                    />
                  ) : (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {selected.roles.map((assignment) => (
                        <Space
                          key={`${assignment.roleId}-${assignment.scopeType}-${assignment.scopeId ?? ''}`}
                          align="start"
                          style={{ width: '100%', justifyContent: 'space-between' }}
                        >
                          <Space direction="vertical" size={0}>
                            <Typography.Text strong>{assignmentRoleName(assignment)}</Typography.Text>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {assignmentScope(assignment)}
                            </Typography.Text>
                          </Space>
                          <Can permission="user.role.assign">
                            {/* ⚠️ O'Z ROLINI TORTIB OLISH TO'SILGAN — sinf
                                javadoc'idagi 3-qoida. */}
                            <Tooltip title={isSelf ? t('identity.self.cannotRevoke') : undefined}>
                              <Button
                                size="small"
                                danger
                                disabled={isSelf}
                                icon={<DeleteOutlined />}
                                onClick={() => confirmRevoke(selected, assignment)}
                              >
                                {t('identity.roles.revoke')}
                              </Button>
                            </Tooltip>
                          </Can>
                        </Space>
                      ))}
                    </Space>
                  )}
                </Card>
              )}

              <UserPermissionsPanel
                user={selected}
                roles={roles.data ?? []}
                catalog={catalog.data ?? []}
                loading={canReadRoles && roles.isPending}
                rolesUnavailable={!canReadRoles || roles.isError}
              />
            </Space>
          )}
        </Col>
      </Row>

      <UserFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          setPicked(created)
          // ⚠️ Yaratilgan xodimda ROL YO'Q: u kiradi va hech narsa
          // ko'rmaydi. Oyna o'zi ochilmasa, bu ikkinchi qadam unutilardi
          // va sabab hech qayerda ko'rinmasdi.
          if (can('user.role.assign') && canReadRoles) setRoleOpen(true)
        }}
      />

      <PasswordModal
        open={passwordOpen}
        user={selected}
        isSelf={isSelf}
        onClose={() => setPasswordOpen(false)}
      />

      <AssignRoleModal
        open={roleOpen}
        user={selected}
        roles={roles.data ?? []}
        onClose={() => setRoleOpen(false)}
      />

      <PartyNodeModal
        open={partyNodeOpen}
        user={selected}
        onClose={() => setPartyNodeOpen(false)}
      />

      <UserNetworkModal open={networkOpen} user={selected} onClose={() => setNetworkOpen(false)} />
    </>
  )
}
